import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import { useAppContext } from "../../contexts/AppContext";
import PackagePaymentView from "../packagePaymentView/PackagePaymentView";
import BookingStageNav from "./BookingStageNav";
import "../packageProfileMockup/PackageProfileMockupPage.css";
import { formatPaymentIls } from "../packagePaymentView/formatPaymentIls";
import { getUserDetails, isAdminUser } from "../../services/authService";
import { useHubwayzHotelSaleCommission } from "./hubwayz/useHubwayzHotelSaleCommission";
import { hubwayzFxBlocksPayment } from "./hubwayz/resolveHubwayzHotelIlsPricing";
import { hubwayzQuoteBlocksPayment } from "./hubwayz/hubwayzVerifiedQuote";
import { buildHubwayzHotelsPaymentPreview } from "./hubwayz/hubwayzSelectedOfferCurrency";
import { HUBWAYZ_CERT_TEST_VOUCHER_EMAIL } from "./hubwayz/hubwayzCertVoucher";
import { isHubwayzUiTestHotelsBookArmed } from "./hubwayz/hubwayzUiTestHotelsBookUnlock";
import { persistHubwayzHotelOrder } from "../../services/hubwayzHotelsApi";
import { refuseHotelSupplierBookBeforePayment } from "./hubwayz/refuseHotelSupplierBookBeforePayment";
import "../../Pages/Client/PackagesProfile/PackagePayment/PackageBankTransferInstructions.scss";

/**
 * Hotels-only Hubwayz Book confirmation (Phase 2P).
 * LDS / flights / customer payment / wallet / ledger are never invoked.
 */
export default function FlightHotelPaymentPage({
  bookingDraft,
  appliedSearch,
  hubwayzSearchRequest = null,
  viewerMode = "agent",
  onBack,
  onStage,
  onDone,
}) {
  const navigate = useNavigate();
  const {
    currencyRate,
    currencyRateSource,
    globalConstant,
    gcKey,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
  } = useAppContext();
  const hrCommissionHotelsSetting = globalConstant?.[gcKey.HR_COMMISSION_HOTELS];
  const agentSaleCommission = useHubwayzHotelSaleCommission();
  const { order, financial, customerPrice } = useMemo(
    () =>
      buildHubwayzHotelsPaymentPreview(bookingDraft, appliedSearch, currencyRate, {
        currencyRateSource,
        getHrCommissionPercent,
        getHrCommissionPercentHotels,
        hrCommissionHotelsSetting,
        getAgentCommissionPercent,
        getCreditCardFeePercent,
        agentCommission: agentSaleCommission,
      }),
    [
      bookingDraft,
      appliedSearch,
      currencyRate,
      currencyRateSource,
      getHrCommissionPercent,
      getHrCommissionPercentHotels,
      hrCommissionHotelsSetting,
      getAgentCommissionPercent,
      getCreditCardFeePercent,
      agentSaleCommission,
    ]
  );
  const fxBlocked = bookingDraft?.hotelsOnly ? false : hubwayzFxBlocksPayment(customerPrice);
  const verifiedQuote = bookingDraft?.hubwayzVerifiedQuote || null;
  const quoteBlocked = bookingDraft?.hotelsOnly === true && hubwayzQuoteBlocksPayment(verifiedQuote);
  const quoteBlockMessage = quoteBlocked
    ? "לא ניתן להמשיך לתשלום בלי אימות מחיר מהשרת."
    : null;
  const displayFinancial =
    bookingDraft?.hotelsOnly === true
      ? verifiedQuote?.binding === true
        ? {
            ...financial,
            totalAmount: verifiedQuote.chargeIls,
            remainingBalance: verifiedQuote.chargeIls,
          }
        : { ...financial, totalAmount: null, remainingBalance: null }
      : financial;
  const fxBlockMessage = fxBlocked
    ? "לא ניתן להמשיך לתשלום: מטבע הספק לא מוכר או שאין שער המרה תקין."
    : null;

  const [termsOk, setTermsOk] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uiState, setUiState] = useState("idle");
  const [toast, setToast] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progressLabel, setProgressLabel] = useState("");
  const [resultMeta, setResultMeta] = useState(null);
  const [draftOverride, setDraftOverride] = useState(null);
  const activeDraft = draftOverride || bookingDraft;

  useEffect(() => {
    setDraftOverride(null);
  }, [bookingDraft]);

  const isLive = activeDraft?.source === "live";
  const prebookToken = activeDraft?.prebook?.token || null;
  const liveBookArmed = isHubwayzUiTestHotelsBookArmed();
  const hasFlightSlice = !!(
    bookingDraft?.flightLabel ||
    bookingDraft?.flightOptionLabel ||
    bookingDraft?.outbound ||
    bookingDraft?.inbound ||
    bookingDraft?.rawDeal
  );

  const resolvedSearchRequest =
    (bookingDraft?.searchRequest &&
    typeof bookingDraft.searchRequest === "object"
      ? bookingDraft.searchRequest
      : null) ||
    (hubwayzSearchRequest && typeof hubwayzSearchRequest === "object"
      ? hubwayzSearchRequest
      : null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const runHotelsOnlyBook = useCallback(async () => {
    if (quoteBlocked) {
      setActionError(quoteBlockMessage);
      return;
    }
    if (fxBlocked) {
      setActionError(fxBlockMessage);
      return;
    }
    if (!termsOk) {
      setFieldErrors({ terms: "יש לאשר את תנאי ההזמנה ותנאי הביטול" });
      return;
    }
    setFieldErrors({});
    setActionError(null);
    setProgressLabel("");
    setBusy(true);
    setUiState("processing");

    const meta = {
      hotelHold: null,
      hotelHoldError: null,
      hotelsOnly: true,
      flightsIncluded: false,
      flightsWarning: hasFlightSlice
        ? "טיסות המוצגות בסיכום אינן כלולות ואינן מוזמנות"
        : null,
      ldsProposal: {
        ok: true,
        skipped: true,
        reason: "hubwayz_hotels_only_isolation",
      },
      ldsOperations: [],
      paymentOperations: [],
      hubwayzDryRun: false,
      networkBookCalled: false,
      persist: null,
      syncPending: false,
      demo: !isLive,
    };

    try {
      if (!isLive) {
        meta.hotelHold = { ok: true, demo: true, note: "דמו · שריון מלון מדומה" };
        setResultMeta(meta);
        setUiState("success");
        showToast("דמו · הזמנת מלון מדומה");
        return;
      }

      const refused = refuseHotelSupplierBookBeforePayment();
      meta.networkBookCalled = refused.networkBookCalled;
      setResultMeta(meta);
      setUiState("idle");
      setActionError(refused.message);
      return;

    } finally {
      setBusy(false);
      setProgressLabel("");
    }
  }, [
    termsOk,
    isLive,
    prebookToken,
    activeDraft,
    bookingDraft,
    appliedSearch,
    resolvedSearchRequest,
    hasFlightSlice,
    liveBookArmed,
    showToast,
    fxBlocked,
    fxBlockMessage,
    quoteBlocked,
    quoteBlockMessage,
    navigate,
  ]);

  const confirmed = uiState === "success" && resultMeta && !resultMeta.hubwayzDryRun;
  const syncPending = !!resultMeta?.syncPending;

  if (confirmed) {
    const n = resultMeta.normalized || resultMeta.hotelHold?.normalized || {};
    const bookingId =
      resultMeta.hotelHold?.bookingId || n.bookingID || "—";
    const supplierRef =
      resultMeta.hotelHold?.supplierReference || n.supplierReference || "—";
    const hrRef =
      resultMeta.hrOrderNumber ||
      (resultMeta.hrOrderId != null ? String(resultMeta.hrOrderId) : null);
    const hotelName =
      n.hotel?.name || bookingDraft?.hotelName || order?.hotelName || "—";
    const roomName =
      n.room?.name ||
      [n.room?.boardName || n.room?.board, n.room?.bedding]
        .filter(Boolean)
        .join(" · ") ||
      bookingDraft?.roomName ||
      "—";
    const checkIn = n.checkIn || appliedSearch?.checkIn || "—";
    const checkOut = n.checkOut || appliedSearch?.checkOut || "—";
    const price = n.price
      ? `${n.price.amount} ${n.price.currency || ""}`.trim()
      : formatPaymentIls(financial?.remainingBalance);
    const isAgent = viewerMode !== "customer" && viewerMode !== "public";

    return (
      <div className="usm-fh-confirm" dir="rtl" style={{ maxWidth: 720, margin: "24px auto", padding: 24 }}>
        <img
          src={HOTELSRIVER_OFFICIAL_LOGO_URL}
          alt="HotelsRiver"
          style={{ height: 40, marginBottom: 16 }}
        />
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>
          {syncPending
            ? "הזמנת הספק אושרה — סנכרון פנימי ממתין"
            : "הזמנת המלון אושרה"}
        </h1>
        <p style={{ color: "#555", marginBottom: 16 }}>
          מלון בלבד · ללא טיסות / ללא חיוב לקוח
        </p>
        {hasFlightSlice ? (
          <p role="status" style={{ background: "#fff7e6", padding: 12, borderRadius: 8 }}>
            שימו לב: טיסות שמופיעות בסיכום <strong>אינן כלולות</strong> ואינן
            מוזמנות בהזמנה זו.
          </p>
        ) : null}
        <ul className="ppm-summary__facts" style={{ marginTop: 16 }}>
          {hrRef ? (
            <li>
              <span className="ppm-summary__fact-label">מס׳ הזמנת HR</span>
              <span className="ppm-summary__fact-value" dir="ltr">{hrRef}</span>
            </li>
          ) : null}
          <li>
            <span className="ppm-summary__fact-label">מספר הזמנה</span>
            <span className="ppm-summary__fact-value" dir="ltr">{bookingId}</span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">Supplier ref</span>
            <span className="ppm-summary__fact-value" dir="ltr">{supplierRef}</span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">מלון / חדר</span>
            <span className="ppm-summary__fact-value">
              {hotelName} · {roomName}
            </span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">תאריכים</span>
            <span className="ppm-summary__fact-value" dir="ltr">
              {checkIn} → {checkOut}
            </span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">סכום</span>
            <span className="ppm-summary__fact-value" dir="ltr">{price}</span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">סטטוס תשלום לקוח</span>
            <span className="ppm-summary__fact-value">לא שולם (account_credit ≠ תשלום לקוח)</span>
          </li>
          <li>
            <span className="ppm-summary__fact-label">שובר</span>
            <span className="ppm-summary__fact-value" dir="ltr">
              {HUBWAYZ_CERT_TEST_VOUCHER_EMAIL}
            </span>
          </li>
          {n.cancellationType ? (
            <li>
              <span className="ppm-summary__fact-label">ביטול</span>
              <span className="ppm-summary__fact-value">
                {n.cancellationType}
                {n.cancellationDeadline ? ` · עד ${n.cancellationDeadline}` : ""}
              </span>
            </li>
          ) : null}
        </ul>
        <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
          {syncPending ? (
            <button
              type="button"
              className="ppm-btn-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const user = getUserDetails() || {};
                  const agentId =
                    user.id ??
                    user.agentId ??
                    user.agent_id ??
                    user.agentid ??
                    null;
                  const retry = await persistHubwayzHotelOrder({
                    bookingId: String(bookingId),
                    agentId,
                    attemptId: resultMeta?.attemptId,
                    correlationId: "ui-sync-retry",
                    voucherEmail: HUBWAYZ_CERT_TEST_VOUCHER_EMAIL,
                    paymentMethod: "account_credit",
                    allowAuthenticatedTestSupplierPersist: true,
                  });
                  if (retry?.ok) {
                    setResultMeta({
                      ...resultMeta,
                      syncPending: false,
                      persist: retry,
                      hrOrderNumber: retry.hr_order_number || null,
                      hrOrderId: retry.order_id || null,
                      bookingID: bookingId,
                      bookingId,
                    });
                    showToast("סנכרון הושלם — ההזמנה נשמרה ב-Orders");
                  } else {
                    showToast(
                      `סנכרון עדיין נכשל: ${retry?.error || retry?.code || "unknown"}`
                    );
                  }
                } finally {
                  setBusy(false);
                }
              }}
            >
              נסה סנכרון שוב
            </button>
          ) : null}
          <button
            type="button"
            className="ppm-btn-primary"
            onClick={() => {
              onDone?.(resultMeta);
              const q = encodeURIComponent(
                String(hrRef || resultMeta.hrOrderId || bookingId || "")
              );
              if (resultMeta.hrOrderId) {
                navigate(`/p/dashboard/orders/hotels/${resultMeta.hrOrderId}`);
              } else {
                navigate(`/p/dashboard/orders?view=all&search=${q}`);
              }
            }}
          >
            צפייה בהזמנה
          </button>
          <button
            type="button"
            className="ppm-btn-secondary"
            onClick={() => {
              onDone?.(resultMeta);
              navigate(
                isAgent || isAdminUser()
                  ? "/p/dashboard/orders?view=all"
                  : "/p/dashboard"
              );
            }}
          >
            חזרה להזמנות
          </button>
        </div>
        {toast ? (
          <p role="status" style={{ marginTop: 16 }}>
            {toast}
          </p>
        ) : null}
      </div>
    );
  }

  const bankSlot = (
    <div className="usm-fh-bank">
      <p className="ppm-section__text">
        <strong>מלון בלבד</strong> — לחיצה מאשרת הזמנת מלון אצל
        הספק. אין טיסות ואין חיוב לקוח.
      </p>
      {hasFlightSlice ? (
        <p className="ppm-section__meta" role="status" style={{ background: "#fff7e6", padding: 10 }}>
          אזהרה: בסיכום מוצגות טיסות לחבילה — הן <strong>לא כלולות</strong> ולא
          יוזמנו. זו הזמנת מלון בלבד, לא חבילת טיסה+מלון מאושרת.
        </p>
      ) : null}
      {liveBookArmed ? (
        <p className="ppm-section__meta" role="status">
          הזמנה חיה פעילה. אמצעי תשלום: אשראי חשבון.
        </p>
      ) : (
        <p className="ppm-section__meta" role="status">
          ההזמנה נעולה ולא תישלח עד לפתיחה.
        </p>
      )}
      <p className="ppm-section__meta">
        יתרה לתצוגה (ללא חיוב):{" "}
        <strong dir="ltr">{formatPaymentIls(financial.remainingBalance)}</strong>
      </p>

      <ul className="ppm-summary__facts" style={{ marginTop: 12 }}>
        <li>
          <span className="ppm-summary__fact-label">מלון</span>
          <span className="ppm-summary__fact-value">
            {prebookToken || !isLive
              ? liveBookArmed
                ? "מוכן להזמנה (prebook)"
                : "מוכן ל־dry-run (prebook)"
              : "חסר prebook"}
          </span>
        </li>
        <li>
          <span className="ppm-summary__fact-label">טיסות LDS</span>
          <span className="ppm-summary__fact-value">לא כלול · לא יוזמן</span>
        </li>
      </ul>

      <div className="ppm-action-block" style={{ marginTop: 16 }}>
        <label className="ppm-terms">
          <input
            type="checkbox"
            checked={termsOk}
            disabled={busy || uiState === "success"}
            onChange={(e) => setTermsOk(e.target.checked)}
            aria-invalid={!!fieldErrors.terms}
          />
          <span>אני מאשר/ת את התקנון, תנאי ההזמנה וגילוי נאות</span>
        </label>
        {fieldErrors.terms ? (
          <p className="ppm-field-error" role="alert">
            {fieldErrors.terms}
          </p>
        ) : null}
        {quoteBlockMessage && uiState !== "success" ? (
          <p className="ppm-field-error" role="alert">
            {quoteBlockMessage}
          </p>
        ) : null}
        {fxBlockMessage && uiState !== "success" ? (
          <p className="ppm-field-error" role="alert">
            {fxBlockMessage}
          </p>
        ) : null}
        {actionError && uiState !== "success" ? (
          <p className="ppm-field-error" role="alert">
            {actionError}
          </p>
        ) : null}

        <button
          type="button"
          className="ppm-btn-primary ppm-btn-primary--block"
          disabled={busy || !termsOk || uiState === "success" || fxBlocked || quoteBlocked}
          onClick={runHotelsOnlyBook}
          data-testid="hubwayz-hotels-only-book-btn"
        >
          {busy ? (
            <>
              <LoadingOutlined spin /> {progressLabel || "מזמין מלון…"}
            </>
          ) : liveBookArmed ? (
            "אשר הזמנת מלון"
          ) : (
            "אימות Book (dry-run) · ללא שליחה"
          )}
        </button>
      </div>
    </div>
  );

  const bookingProduct = bookingDraft?.hotelsOnly ? "hotel" : "flightHotel";

  return (
    <div className="hpm-mock" dir="rtl" lang="he">
      <header className="hpm-topbar">
        <div className="hpm-topbar__brand">
          <img src={HOTELSRIVER_OFFICIAL_LOGO_URL} alt="HotelsRiver" />
          <span className="hpm-pill">
            {bookingDraft?.hotelsOnly ? "מלון" : "טיסה + מלון"}
          </span>
        </div>
        <BookingStageNav
          product={bookingProduct}
          activeId="payment"
          onStage={(id) => onStage?.(id)}
        />
      </header>
    <PackagePaymentView
      order={order}
      financial={displayFinancial}
      brand={{
        logoUrl: HOTELSRIVER_OFFICIAL_LOGO_URL,
        name: "HotelsRiver",
        alt: "HotelsRiver",
        thanksTeam: "צוות HotelsRiver",
      }}
      viewerMode={viewerMode === "public" ? "customer" : viewerMode}
      isPublic={viewerMode === "public" || viewerMode === "customer"}
      uiState={uiState === "processing" || uiState === "success" ? "idle" : uiState}
      method="bank"
      lockMethodToBank
      payPath="link"
      cards={[]}
      activeCard={0}
      cardCount={1}
      termsOk={termsOk}
      fieldErrors={fieldErrors}
      busy={busy}
      actionError={quoteBlockMessage || fxBlockMessage || actionError}
      onBack={onBack}
      onMethodChange={() => {}}
      onTermsChange={setTermsOk}
      onPrimaryAction={() => {
        if (quoteBlocked) {
          setActionError(quoteBlockMessage);
          return;
        }
        if (fxBlocked) {
          setActionError(fxBlockMessage);
          return;
        }
        runHotelsOnlyBook();
      }}
      bankSlot={bankSlot}
      toast={toast}
      currencyRate={currencyRate}
      paymentExchangeRate={(() => {
        const display = String(bookingDraft?.displayCurrency || "").trim().toUpperCase();
        if (display && display !== "ILS") {
          const rate = Number(currencyRate?.[display]);
          return Number.isFinite(rate) && rate > 0 ? rate : null;
        }
        return customerPrice?.ok && customerPrice.supplierCurrency !== "ILS"
          ? customerPrice.fxRate
          : Number(currencyRate?.USD) || null;
      })()}
      paymentExchangeCurrency={(() => {
        const display = String(bookingDraft?.displayCurrency || "").trim().toUpperCase();
        if (display && display !== "ILS") return display;
        return customerPrice?.ok ? customerPrice.supplierCurrency : null;
      })()}
    />
    </div>
  );
}
